import { Box, Button, Heading, RadioGroup, RadioGroupProps, Text } from '@wix/design-system'
import { useContent, useNext } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../../bi/mfi'
import { useBrowserTabTitle } from '../../../../hooks/useBrowserTabTitle'
import { WixStudioMobileHeader } from '../../../reusable/WixStudioMobileHeader'
import type { Content } from './Content'
import { dataHooks } from './WixStudioMobileRadioGroup.dataHooks'
import s from './WixStudioMobileRadioGroup.scss'

type Props = {
  onNext?: (value?: string) => void
  prepend?: (value?: string) => React.ReactNode
}

export const WixStudioMobileRadioGroup: FC<Props> = ({ onNext = () => {}, prepend = () => null }) => {
  const { content } = useContent<Content>()
  const { questionKey } = content.question
  useBrowserTabTitle(content.browserTabTitle)

  const pageLogger = usePageLogger()
  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const next = useNext()
  const [value, setValue] = useState<string | undefined>(undefined)

  const handleChange: RadioGroupProps['onChange'] = newValue => {
    void pageLogger.clickAnswer({
      questionKey,
      buttonName: newValue as string,
    })
    setValue(newValue as string)
  }

  const handleNext = () => {
    if (!value) {
      console.error('No value is selected when Next button is clicked')
      return
    }

    void Promise.all([pageLogger.answer({ answer: value }), onNext(value)])
    next({ [questionKey]: { values: [value] } })
  }

  const handleSkip = () => {
    void Promise.all([pageLogger.skip(), onNext(value)])
    next({ [questionKey]: { skipped: true } })
  }

  return (
    <Box direction="vertical" height="100vh" width="100vw" className={s.container}>
      <WixStudioMobileHeader skip={content.skip} onSkipClick={handleSkip} />
      <Box direction="vertical" width="100%" align="center" padding="20px" boxSizing="border-box">
        {prepend(value)}
        <Box textAlign="center">
          <Text size="small" dataHook={dataHooks.subtitleAbove()} className={s.subtitle}>
            {content.subtitleAbove}
          </Text>
        </Box>
        <Box marginTop="SP2" marginBottom="SP6" textAlign="center">
          <Heading size="large" className={s.title} dataHook={dataHooks.title()}>
            {content.title}
          </Heading>
        </Box>
        <Box direction="vertical" width="100%">
          <RadioGroup
            value={value}
            onChange={handleChange}
            selectionAreaPadding="10px 20px"
            selectionArea="always"
            selectionAreaSkin="outlined"
            spacing="12px"
            className={s.radioGroup}
            dataHook={dataHooks.radioGroup()}
          >
            {content.question.options.map(({ optionKey, label }) => {
              return (
                <RadioGroup.Radio key={optionKey} value={optionKey}>
                  {label}
                </RadioGroup.Radio>
              )
            })}
          </RadioGroup>
        </Box>
        <div className={s.buttonContainer}>
          <Button
            size="large"
            fullWidth
            disabled={value === undefined}
            onClick={handleNext}
            dataHook={dataHooks.next()}
          >
            {content.next}
          </Button>
        </div>
      </Box>
    </Box>
  )
}
